/**
 * My Day — the owner's private daily planner.
 *
 * Owner-only in three independent places: the route guard in App.js, the nav
 * filter in GlobalNav, and the Firestore rule on myDayData/{uid}, which grants
 * a signed-in owner access to their own document and nobody else's. Even
 * another owner account cannot read this one; the uid is part of the path.
 *
 * The idea the page is built around: anything you don't finish is still yours
 * tomorrow. Open work from past days rolls onto today automatically, carrying a
 * counter so a task you've dodged five times says so out loud.
 *
 * Everything lives in one Firestore document (see myDayCloud.js), which means
 * the page keeps working on a phone with no signal and reconciles when it comes
 * back.
 */
import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import {
  todayStr, addDays, relativeDay, fmtDay, fmtDuration, diffDays,
  DEFAULT_CATEGORIES, DEFAULT_SETTINGS, PRIORITIES,
  makeTask, parseQuickAdd, rollForward, generateRoutineTasks, sortTasks, matchesSearch, tasksOnDay,
  dayLoad, dayProgress, computeStats, csvColumns, uid,
  dueReminders, reminderKey, reminderLead, staleTasks, tidyStale, restoreTasks,
  trashTasks, restoreFromTrash, reorderTask, canMove, myDayPayload, payloadSig,
  pruneTasks, pruneTrash,
} from './myDayUtils';
import {
  notifyPermission, requestNotifyPermission, showLocalNotification,
} from '../../utils/localNotify';
import { exportCsv as downloadCsvFile } from '../Tickets/lib/csv';
import { subscribeMyDay, saveMyDay } from './myDayCloud';
import QuickAdd from './components/QuickAdd';
import TaskRow from './components/TaskRow';
import TaskEditor from './components/TaskEditor';
import FocusTimer from './components/FocusTimer';
import MatrixView from './components/MatrixView';
import HabitsView from './components/HabitsView';
import ReviewView from './components/ReviewView';
import InsightsView from './components/InsightsView';
import RoutinesView from './components/RoutinesView';
import UpcomingView from './components/UpcomingView';
import PlanView from './components/PlanView';
import './MyDay.css';

const VIEWS = [
  { id: 'today',    label: 'Today',    icon: '☀️' },
  { id: 'plan',     label: 'Plan',     icon: '🧭' },
  { id: 'upcoming', label: 'Upcoming', icon: '🗓️' },
  { id: 'matrix',   label: 'Matrix',   icon: '⚖️' },
  { id: 'habits',   label: 'Habits',   icon: '🔥' },
  { id: 'routines', label: 'Routines', icon: '🔁' },
  { id: 'review',   label: 'Review',   icon: '🌙' },
  { id: 'insights', label: 'Insights', icon: '📈' },
];

const SAVE_DEBOUNCE_MS = 700;

export default function MyDay() {
  const { user } = useAuth();

  // ── Planner state (mirrors the single Firestore document) ────────────────
  const [tasks, setTasks]       = useState([]);
  const [routines, setRoutines] = useState([]);
  const [habits, setHabits]     = useState([]);
  const [days, setDays]         = useState({});
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [cats, setCats]         = useState(DEFAULT_CATEGORIES);
  const [trash, setTrash]       = useState([]);

  // ── UI state ─────────────────────────────────────────────────────────────
  const [view, setView]         = useState('today');
  const [date, setDate]         = useState(todayStr());
  const [today, setToday]       = useState(todayStr());
  const [search, setSearch]     = useState('');
  const [catFilter, setCatFilter]   = useState('all');
  const [priFilter, setPriFilter]   = useState('all');
  const [showDone, setShowDone]     = useState(true);
  const [editingId, setEditingId]   = useState(null);
  const [focus, setFocus]       = useState(null);
  const [sync, setSync]         = useState('loading');
  const [toast, setToast]       = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [notifyState, setNotifyState] = useState(notifyPermission);
  const [tidyOpen, setTidyOpen] = useState(false);
  const [wipeOpen, setWipeOpen] = useState(false);
  const [wipeConfirm, setWipeConfirm] = useState('');
  const [planDate, setPlanDate]   = useState(() => addDays(todayStr(), 1));

  const hydrated  = useRef(false);
  // payloadSig of what the cloud doc holds (last applied or last written); the
  // save effect skips state that already matches it.
  const syncedSig = useRef(null);
  // React 18 StrictMode double-invokes state updaters, so the roll-forward is
  // computed from a ref and committed with plain setState calls — an updater
  // that also wrote the day ledger would count every carry twice.
  const tasksRef  = useRef([]);
  const focusRef  = useRef(null);
  const saveTimer = useRef(null);
  const quickRef  = useRef(null);
  const toastTimer = useRef(null);
  const firedReminders = useRef(new Set());
  const undoRef = useRef(null);
  const trashRef = useRef([]);
  // Ids of the group the row being acted on belongs to, in screen order.
  const siblingsRef = useRef([]);

  useEffect(() => { tasksRef.current = tasks; }, [tasks]);
  useEffect(() => { focusRef.current = focus; }, [focus]);
  useEffect(() => { trashRef.current = trash; }, [trash]);

  const flash = useCallback((msg, undo = null) => {
    undoRef.current = undo;
    setToast({ msg, undo: !!undo });
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => { setToast(null); undoRef.current = null; }, undo ? 8000 : 4000);
  }, []);

  // ── Cloud: one listener in, one debounced write out ──────────────────────
  useEffect(() => {
    if (!user?.uid) return undefined;
    hydrated.current = false;
    syncedSig.current = null;
    setSync('loading');
    const unsub = subscribeMyDay(user.uid, (data, meta) => {
      if (data) {
        // Pruned here as well as on save: the old code's load-time write pruned the
        // doc and its echo swapped in the pruned lists, so expired trash and
        // long-done work never stayed on screen. Without that write, do it here.
        const next = {
          tasks: pruneTasks(Array.isArray(data.tasks) ? data.tasks : []),
          routines: Array.isArray(data.routines) ? data.routines : [],
          habits: Array.isArray(data.habits) ? data.habits : [],
          days: data.days && typeof data.days === 'object' ? data.days : {},
          settings: { ...DEFAULT_SETTINGS, ...(data.settings || {}) },
          categories: Array.isArray(data.categories) && data.categories.length ? data.categories : DEFAULT_CATEGORIES,
          trash: pruneTrash(Array.isArray(data.trash) ? data.trash : []),
        };
        const sig = payloadSig(myDayPayload(next));
        // Our own write coming back from the server (its serverTimestamp resolved,
        // so it isn't flagged as a pending write) — the page already has this.
        // Re-applying it made fresh arrays, which re-triggered the save below: a
        // write-and-read loop about once a second for as long as My Day was open,
        // and it could also roll back an edit typed while the write was in flight.
        if (sig !== syncedSig.current) {
          syncedSig.current = sig;
          setTasks(next.tasks);
          setRoutines(next.routines);
          setHabits(next.habits);
          setDays(next.days);
          setSettings(next.settings);
          setCats(next.categories);
          setTrash(next.trash);
        }
      }
      hydrated.current = true;
      setSync(meta.fromCache ? 'offline' : 'synced');
    }, err => {
      console.error('My Day sync:', err);
      hydrated.current = true;
      setSync('error');
    });
    return () => { unsub(); clearTimeout(saveTimer.current); };
  }, [user?.uid]);

  useEffect(() => {
    if (!user?.uid || !hydrated.current) return undefined;
    const state = { tasks, routines, habits, days, settings, categories: cats, trash };
    const sig = payloadSig(myDayPayload(state));
    // Only a real edit is written. State that matches what the cloud already
    // holds — a fresh load, or our own write echoing back — costs nothing.
    // (An edit undone inside the debounce lands here too: its pending save was
    // just cancelled, so don't leave the badge saying "saving".)
    if (sig === syncedSig.current) {
      setSync(s => (s === 'saving' ? 'synced' : s));
      return undefined;
    }
    setSync(s => (s === 'offline' ? s : 'saving'));
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      syncedSig.current = sig;
      saveMyDay(user.uid, state)
        .then(() => setSync('synced'))
        .catch(err => {
          console.error('My Day save:', err);
          syncedSig.current = null;   // not in the cloud — let the next change retry it
          setSync('error');
        });
    }, SAVE_DEBOUNCE_MS);
    return () => clearTimeout(saveTimer.current);
  }, [user?.uid, tasks, routines, habits, days, settings, cats, trash]);

  // ── Carry forward + routine generation ───────────────────────────────────
  const runDailyMaintenance = useCallback(() => {
    const now = todayStr();
    setToday(prev => (prev === now ? prev : now));

    let next = tasksRef.current;
    let carried = 0;
    let ledger = null;
    if (settings.autoCarry !== false) {
      const rolled = rollForward(next, now);
      if (rolled.moved.length) {
        next = rolled.tasks;
        carried = rolled.moved.length;
        ledger = rolled.movedByDate;
      }
    }
    const fresh = generateRoutineTasks(routines, next, now);
    if (fresh.length) next = [...next, ...fresh];
    if (next === tasksRef.current) return;

    tasksRef.current = next;
    setTasks(next);
    if (ledger) {
      // Stamp what left each day, so a day the owner abandoned doesn't look
      // perfect in hindsight once its work has moved onto today.
      setDays(prev => {
        const out = { ...prev };
        Object.entries(ledger).forEach(([d, n]) => {
          out[d] = { ...(out[d] || {}), rolled: (out[d]?.rolled || 0) + n };
        });
        return out;
      });
    }
    if (carried) {
      flash(`↪ ${carried} unfinished ${carried === 1 ? 'activity' : 'activities'} carried forward to today.`);
    }
  }, [routines, settings.autoCarry, flash]);

  // Run once the cloud data lands, then on a timer and whenever the tab wakes,
  // so a screen left open overnight still greets the owner with a fresh day.
  useEffect(() => {
    if (sync === 'loading') return undefined;
    runDailyMaintenance();
    const id = setInterval(runDailyMaintenance, 60000);
    const onWake = () => { if (!document.hidden) runDailyMaintenance(); };
    document.addEventListener('visibilitychange', onWake);
    window.addEventListener('focus', onWake);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', onWake);
      window.removeEventListener('focus', onWake);
    };
  }, [sync, runDailyMaintenance]);

  // ── Deleting, and getting it back ────────────────────────────────────────
  // Nothing is ever dropped straight out of the planner. A delete moves the
  // activity to the trash — undoable from the toast for the next few seconds,
  // and restorable from the Review tab for thirty days after that.
  const moveToTrash = useCallback((ids, message) => {
    if (!ids.length) return;
    const result = trashTasks(tasksRef.current, ids, trashRef.current);
    setTasks(result.tasks);
    setTrash(result.trash);
    flash(message, () => {
      const back = restoreFromTrash(result.tasks, result.trash, ids);
      setTasks(back.tasks);
      setTrash(back.trash);
      flash('Put back.');
    });
  }, [flash]);

  const restoreTrashed = useCallback(ids => {
    const back = restoreFromTrash(tasksRef.current, trashRef.current, ids);
    setTasks(back.tasks);
    setTrash(back.trash);
    flash(ids.length === 1
      ? `Restored “${back.restored[0]?.title || 'the activity'}”.`
      : `Restored ${ids.length} activities.`);
  }, [flash]);

  /** Out of the trash for good — this one really is gone. */
  const purgeTrashed = useCallback(ids => {
    const set = new Set(ids);
    setTrash(prev => prev.filter(t => !set.has(t.id)));
  }, []);

  const emptyTrash = useCallback(() => {
    const count = trashRef.current.length;
    if (!count) return;
    if (!window.confirm(`Permanently delete ${count} ${count === 1 ? 'activity' : 'activities'}? This can't be undone.`)) return;
    setTrash([]);
    flash(`Trash emptied — ${count} ${count === 1 ? 'activity' : 'activities'} gone for good.`);
  }, [flash]);

  const clearCompleted = useCallback(() => {
    const ids = tasksRef.current
      .filter(t => t.done && !t.archived && (t.doneDate || t.date) === date)
      .map(t => t.id);
    moveToTrash(ids, `Cleared ${ids.length} completed ${ids.length === 1 ? 'activity' : 'activities'}.`);
  }, [date, moveToTrash]);

  /** The whole planner, wiped. Guarded by a typed confirmation, never a stray click. */
  const wipeEverything = useCallback(() => {
    setTasks([]); setTrash([]); setRoutines([]); setHabits([]); setDays({});
    setWipeOpen(false); setWipeConfirm('');
    firedReminders.current = new Set();
    flash('My Day reset. Everything has been deleted.');
  }, [flash]);

  // ── Task mutations ───────────────────────────────────────────────────────
  const patchTask = useCallback((id, patch) => {
    setTasks(prev => prev.map(t => (t.id === id ? { ...t, ...patch } : t)));
  }, []);

  const addTask = useCallback(draft => {
    const target = draft.date || date;
    if (draft.recurrence) {
      const routine = {
        id: uid(),
        title: draft.title,
        rule: draft.recurrence.type === 'everyN'
          ? { ...draft.recurrence, startDate: target }
          : draft.recurrence,
        priority: draft.priority,
        category: draft.category,
        tags: draft.tags,
        estimateMin: draft.estimateMin,
        startTime: draft.startTime,
        active: true,
        createdAt: Date.now(),
      };
      setRoutines(prev => [...prev, routine]);
      flash(`🔁 Routine saved — it will appear on every due day.`);
      return;
    }
    setTasks(prev => [...prev, makeTask({
      title: draft.title,
      date: target,
      priority: draft.priority,
      category: draft.category,
      tags: draft.tags || [],
      estimateMin: draft.estimateMin,
      startTime: draft.startTime,
      order: prev.length,
    })]);
  }, [date, flash]);

  const toggleTask = useCallback(task => {
    const done = !task.done;
    patchTask(task.id, {
      done,
      doneAt: done ? Date.now() : null,
      doneDate: done ? todayStr() : null,
    });
  }, [patchTask]);

  const taskAction = useCallback((action, task) => {
    switch (action) {
      case 'tomorrow':
        patchTask(task.id, { date: addDays(todayStr(), 1) });
        flash(`→ "${task.title}" moved to tomorrow.`);
        break;
      case 'nextweek':
        patchTask(task.id, { date: addDays(todayStr(), 7) });
        flash(`→ "${task.title}" moved to next week.`);
        break;
      case 'today':
        patchTask(task.id, { date: todayStr() });
        break;
      case 'park':
        patchTask(task.id, { noCarry: !task.noCarry });
        flash(task.noCarry
          ? `"${task.title}" will carry forward again.`
          : `⏸ "${task.title}" parked — it stays on ${relativeDay(task.date, today)}.`);
        break;
      case 'pin':
        patchTask(task.id, { pinned: !task.pinned });
        break;
      case 'up':
      case 'down':
        // The caller knows the group as it appears on screen; the row only
        // knows it wants to move one place.
        setTasks(prev => reorderTask(prev, task.id, action === 'up' ? -1 : 1, siblingsRef.current));
        break;
      case 'focus':
        setFocus({
          taskId: task.id,
          mode: 'work',
          elapsed: 0,
          remaining: settings.pomodoroMin * 60,
          totalSec: settings.pomodoroMin * 60,
          running: true,
        });
        break;
      case 'delete':
        moveToTrash([task.id], `Deleted “${task.title}”.`);
        break;
      default:
        break;
    }
  }, [patchTask, flash, moveToTrash, settings.pomodoroMin, today]);

  const makeRoutineFromTask = useCallback(task => {
    setRoutines(prev => [...prev, {
      id: uid(),
      title: task.title,
      rule: { type: 'daily' },
      priority: task.priority,
      category: task.category,
      tags: task.tags,
      estimateMin: task.estimateMin,
      startTime: task.startTime,
      subtasks: (task.subtasks || []).map(s => ({ title: s.title })),
      active: true,
      createdAt: Date.now(),
    }]);
    setEditingId(null);
    setView('routines');
    flash('🔁 Added as a daily routine — change how often below.');
  }, [flash]);

  // ── Reminders for time-blocked activities ────────────────────────────────
  // A sweep rather than one setTimeout per task: a task can be re-timed,
  // rescheduled or deleted at any moment, and a sweep can't leave a stale timer
  // behind. Fired keys live on the device, so two screens open in the truck
  // both nudge.
  const sweepReminders = useCallback(() => {
    if (settings.remindersOn === false) return;
    const due = dueReminders(tasksRef.current, Date.now(), settings.remindLeadMin, firedReminders.current);
    due.forEach(task => {
      firedReminders.current.add(reminderKey(task));
      const when = reminderLead(task);
      const shown = showLocalNotification(
        `⏰ ${task.title}`,
        `Starts ${when}${task.estimateMin ? ` · about ${fmtDuration(task.estimateMin)}` : ''}`,
        { tag: `myday-${task.id}`, url: `${window.location.origin}/my-day` },
      );
      // No permission (or an unsupported browser) still gets the in-page nudge.
      if (!shown) flash(`⏰ “${task.title}” starts ${when}.`);
    });
  }, [settings.remindersOn, settings.remindLeadMin, flash]);

  // Sweep whenever the list changes — so an activity added for ten minutes'
  // time nudges straight away — and on a timer for the hours in between.
  useEffect(() => { sweepReminders(); }, [sweepReminders, tasks]);
  useEffect(() => {
    const id = setInterval(sweepReminders, 30000);
    return () => clearInterval(id);
  }, [sweepReminders]);

  const askForNotifications = useCallback(async () => {
    const result = await requestNotifyPermission();
    setNotifyState(result);
    if (result === 'granted') {
      showLocalNotification('Reminders are on', 'My Day will nudge you before a timed activity starts.', { tag: 'myday-test' });
      flash('🔔 Reminders enabled for this device.');
    } else if (result === 'denied') {
      flash('Reminders are blocked in the browser — My Day will nudge you on the page instead.');
    }
  }, [flash]);

  // ── Tidying up work that keeps rolling over ──────────────────────────────
  const stale = useMemo(
    () => staleTasks(tasks, settings.staleAfterCarries),
    [tasks, settings.staleAfterCarries]
  );

  const tidyUp = useCallback(mode => {
    const ids = stale.map(t => t.id);
    if (!ids.length) return;
    const { tasks: next, before } = tidyStale(tasksRef.current, ids, mode, todayStr());
    setTasks(next);
    setTidyOpen(false);
    flash(
      mode === 'shelve'
        ? `Shelved ${ids.length} stale ${ids.length === 1 ? 'activity' : 'activities'} — find them under Review.`
        : `Pushed ${ids.length} stale ${ids.length === 1 ? 'activity' : 'activities'} to next week.`,
      () => { setTasks(prev => restoreTasks(prev, before)); flash('Put back.'); }
    );
  }, [stale, flash]);

  const restoreShelved = useCallback(task => {
    patchTask(task.id, { archived: false, shelvedAt: null, date: todayStr(), carryCount: 0 });
    flash(`“${task.title}” is back on today.`);
  }, [patchTask, flash]);

  // ── Planning a day ───────────────────────────────────────────────────────
  /** Move an activity onto the day being planned, giving it a clean run there. */
  const pickIntoDay = useCallback((task, target) => {
    patchTask(task.id, {
      date: target,
      archived: false,
      shelvedAt: null,
      carryCount: 0,
      originalDate: target,
      noCarry: false,
    });
  }, [patchTask]);

  /** Give an activity a start time, or take it off the clock with null. */
  const scheduleTask = useCallback((task, slot) => {
    patchTask(task.id, { startTime: slot });
  }, [patchTask]);

  /**
   * Quick add straight onto a chosen day. An empty string with a slot is the
   * timeline's "+ add at 9:00" affordance: it opens the editor on a new,
   * already-timed activity rather than making the owner type a time.
   */
  const addToDay = useCallback((text, target, slot = null) => {
    const draft = text.trim() ? parseQuickAdd(text, target) : null;
    const task = makeTask({
      title: draft?.title || '',
      date: draft?.date || target,
      priority: draft?.priority ?? 3,
      category: draft?.category ?? null,
      tags: draft?.tags || [],
      estimateMin: draft?.estimateMin ?? null,
      startTime: draft?.startTime || slot,
    });
    setTasks(prev => [...prev, { ...task, order: prev.length }]);
    if (!task.title) setEditingId(task.id); // let them name it in the editor
  }, []);

  // ── Focus timer ──────────────────────────────────────────────────────────
  const focusTask = tasks.find(t => t.id === focus?.taskId) || null;

  // Elapsed focus is counted inside the session and banked onto the task when
  // the session pauses, ends or completes. Writing it on every tick would mean
  // a Firestore write per second for a whole pomodoro. Like the roll-forward,
  // the settling is computed from a ref and committed with plain setState calls
  // — banking inside an updater would double-count under StrictMode.
  const tickFocus = useCallback(() => {
    setFocus(f => (f && f.running && f.remaining > 0
      ? { ...f, remaining: f.remaining - 1, elapsed: (f.elapsed || 0) + 1 }
      : f));
  }, []);

  /** Move the seconds worked so far onto the task. Returns nothing; safe to repeat. */
  const bankFocus = useCallback(() => {
    const session = focusRef.current;
    if (!session || session.mode !== 'work' || !session.elapsed) return;
    const { taskId, elapsed } = session;
    focusRef.current = { ...session, elapsed: 0 };
    setFocus(f => (f ? { ...f, elapsed: 0 } : f));
    setTasks(prev => prev.map(t =>
      (t.id === taskId ? { ...t, focusSec: (t.focusSec || 0) + elapsed } : t)));
  }, []);

  const changeFocus = useCallback(patch => {
    // Pausing or restarting settles the time worked so far, so nothing is lost
    // if the tab is closed mid-session.
    if (patch.running === false || patch.remaining != null) bankFocus();
    setFocus(f => (f ? { ...f, ...patch } : f));
  }, [bankFocus]);

  const stopFocus = useCallback(() => {
    bankFocus();
    setFocus(null);
  }, [bankFocus]);

  const completeFocus = useCallback(() => {
    bankFocus();
    const wasWork = focusRef.current?.mode === 'work';
    const nextMode = wasWork ? 'break' : 'work';
    const mins = nextMode === 'work' ? settings.pomodoroMin : settings.breakMin;
    setFocus(f => (f
      ? { ...f, mode: nextMode, elapsed: 0, remaining: mins * 60, totalSec: mins * 60, running: false }
      : f));
    flash(wasWork ? '🎯 Focus session done — take a break.' : '☕ Break over. Ready for another round?');
  }, [settings.pomodoroMin, settings.breakMin, flash, bankFocus]);

  // ── Day metadata (intention + shutdown review) ───────────────────────────
  const dayMeta = days[date] || {};
  const setDayMeta = useCallback(patch => {
    setDays(prev => ({ ...prev, [date]: { ...(prev[date] || {}), ...patch } }));
  }, [date]);

  // ── Derived lists ────────────────────────────────────────────────────────
  const visible = useMemo(() => tasks.filter(t =>
    !t.archived
    && matchesSearch(t, search)
    && (catFilter === 'all' || t.category === catFilter)
    && (priFilter === 'all' || t.priority === Number(priFilter))
  ), [tasks, search, catFilter, priFilter]);

  const dayTasks = useMemo(() => sortTasks(tasksOnDay(visible, date)), [visible, date]);

  const groups = useMemo(() => {
    const open = dayTasks.filter(t => !t.done);
    return {
      carried: open.filter(t => (t.carryCount || 0) > 0),
      scheduled: open.filter(t => !(t.carryCount > 0) && t.startTime),
      anytime: open.filter(t => !(t.carryCount > 0) && !t.startTime),
      done: dayTasks.filter(t => t.done),
    };
  }, [dayTasks]);

  const progress = dayProgress(visible, date, days);
  const load = dayLoad(dayTasks, settings.capacityMin);
  const stats = computeStats(tasks, today, 14, days);
  const remainingMin = dayTasks.filter(t => !t.done).reduce((s, t) => s + (t.estimateMin || 0), 0);
  const editing = tasks.find(t => t.id === editingId) || null;

  // ── Keyboard shortcuts ───────────────────────────────────────────────────
  useEffect(() => {
    const onKey = e => {
      const tag = (e.target.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea' || tag === 'select' || e.metaKey || e.ctrlKey) return;
      // The editor is a modal; its own Escape handler owns the keyboard there.
      if (editingId) return;
      if (e.key === 'n') { e.preventDefault(); setView('today'); quickRef.current?.focus(); }
      if (e.key === '/') { e.preventDefault(); document.getElementById('md-search')?.focus(); }
      if (e.key === 't') { setDate(todayStr()); setView('today'); }
      if (e.key === 'ArrowLeft')  setDate(d => addDays(d, -1));
      if (e.key === 'ArrowRight') setDate(d => addDays(d, 1));
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [editingId]);

  const exportCsv = () => downloadCsvFile('my-day', csvColumns(cats), tasks);

  const syncLabel = { loading: 'Loading…', saving: 'Saving…', synced: 'Synced', offline: 'Offline — saved locally', error: 'Sync error' }[sync];

  return (
    <div className="md">
      <div className="md__inner">
        {/* ── Hero ───────────────────────────────────────────────────────── */}
        <header className="md__hero">
          <div className="md__hero-left">
            <h1 className="md__title"><span className="md__title-grad">My Day</span></h1>
            <p className="md__sub">
              Your private running list. Anything you don't finish follows you into tomorrow.
            </p>
            <div className="md__daynav">
              <button className="md-iconbtn" onClick={() => setDate(d => addDays(d, -1))} aria-label="Previous day">‹</button>
              <span className="md__daylabel">
                <strong>{relativeDay(date, today)}</strong>
                <em>{fmtDay(date, { weekday: 'long', month: 'long', day: 'numeric' })}</em>
              </span>
              <button className="md-iconbtn" onClick={() => setDate(d => addDays(d, 1))} aria-label="Next day">›</button>
              {date !== today && (
                <button className="md-btn md-btn--sm" onClick={() => setDate(today)}>Jump to today</button>
              )}
              <span className={`md__synctag md__synctag--${sync}`}>{syncLabel}</span>
            </div>
          </div>

          <div className="md__hero-right">
            <div className="md__ring" role="img" aria-label={`${progress.done} of ${progress.total} activities done, ${progress.pct} percent`}>
              <svg viewBox="0 0 120 120" width="112" height="112">
                <circle className="md__ring-track" cx="60" cy="60" r="52" />
                <circle
                  className="md__ring-fill" cx="60" cy="60" r="52"
                  strokeDasharray={`${(progress.pct / 100) * 327} 327`}
                />
              </svg>
              <span className="md__ring-txt">
                <b>{progress.pct}%</b>
                <em>{progress.done}/{progress.total}</em>
              </span>
            </div>
            <div className="md__hero-stats">
              <span title="Days where everything planned got done">🔥 {stats.streak}-day streak</span>
              {remainingMin > 0 && <span title="Estimated time left on this day">⏳ {fmtDuration(remainingMin)} left</span>}
              {stats.carriedOpen > 0 && <span className="md__warn" title="Open items that have already rolled over">↪ {stats.carriedOpen} carried</span>}
            </div>
          </div>
        </header>

        {/* ── View tabs ──────────────────────────────────────────────────── */}
        <nav className="md__tabs" aria-label="Planner views">
          {VIEWS.map(v => (
            <button key={v.id}
              className={`md__tab${view === v.id ? ' md__tab--on' : ''}`}
              onClick={() => setView(v.id)}
              aria-current={view === v.id}
            >
              <span aria-hidden="true">{v.icon}</span> {v.label}
            </button>
          ))}
          <span className="md__tabs-spacer" />
          <button className="md-btn md-btn--sm" onClick={() => setShowSettings(s => !s)} aria-expanded={showSettings}>⚙️ Settings</button>
          <button className="md-btn md-btn--sm" onClick={exportCsv}>⬇ Export CSV</button>
        </nav>

        {showSettings && (
          <section className="md-card md__settings">
            <h3 className="md-card__title">Settings</h3>
            <div className="md-field md-field--row">
              <label className="md-field md-field--grow">
                <span className="md-field__lbl">Daily capacity (minutes of real work)</span>
                <input className="md-input" type="number" min="30" step="30" value={settings.capacityMin}
                  onChange={e => setSettings(s => ({ ...s, capacityMin: Math.max(30, Number(e.target.value) || 360) }))} />
              </label>
              <label className="md-field md-field--grow">
                <span className="md-field__lbl">Focus session (minutes)</span>
                <input className="md-input" type="number" min="5" max="120" value={settings.pomodoroMin}
                  onChange={e => setSettings(s => ({ ...s, pomodoroMin: Math.max(5, Number(e.target.value) || 25) }))} />
              </label>
              <label className="md-field md-field--grow">
                <span className="md-field__lbl">Break (minutes)</span>
                <input className="md-input" type="number" min="1" max="60" value={settings.breakMin}
                  onChange={e => setSettings(s => ({ ...s, breakMin: Math.max(1, Number(e.target.value) || 5) }))} />
              </label>
            </div>
            <label className="md-toggle">
              <input type="checkbox" checked={settings.autoCarry !== false}
                onChange={e => setSettings(s2 => ({ ...s2, autoCarry: e.target.checked }))} />
              <span>Carry unfinished activities forward automatically each morning</span>
            </label>
            <label className="md-toggle">
              <input type="checkbox" checked={settings.remindersOn !== false}
                onChange={e => setSettings(s2 => ({ ...s2, remindersOn: e.target.checked }))} />
              <span>Remind me before a timed activity starts</span>
            </label>
            {settings.remindersOn !== false && (
              <div className="md-field md-field--row">
                <label className="md-field md-field--grow">
                  <span className="md-field__lbl">Nudge me this many minutes ahead</span>
                  <input className="md-input" type="number" min="0" max="120" value={settings.remindLeadMin}
                    onChange={e => setSettings(s2 => ({ ...s2, remindLeadMin: Math.max(0, Number(e.target.value) || 0) }))} />
                </label>
                <label className="md-field md-field--grow">
                  <span className="md-field__lbl">Count as stale after this many carries</span>
                  <input className="md-input" type="number" min="1" max="30" value={settings.staleAfterCarries}
                    onChange={e => setSettings(s2 => ({ ...s2, staleAfterCarries: Math.max(1, Number(e.target.value) || 3) }))} />
                </label>
                <div className="md-field md-field--grow">
                  <span className="md-field__lbl">Browser notifications</span>
                  {notifyState === 'granted' ? (
                    <span className="md-hint">✓ On for this device. Reminders show even when the tab is in the background.</span>
                  ) : notifyState === 'unsupported' ? (
                    <span className="md-hint">This browser can't show notifications — you'll get an on-page nudge instead.</span>
                  ) : notifyState === 'denied' ? (
                    <span className="md-hint">Blocked in the browser. Re-allow it in the site settings; until then you'll get an on-page nudge.</span>
                  ) : (
                    <button className="md-btn md-btn--sm" onClick={askForNotifications}>🔔 Turn on notifications</button>
                  )}
                </div>
              </div>
            )}
            <p className="md-hint">
              Shortcuts: <kbd>N</kbd> new activity · <kbd>/</kbd> search · <kbd>T</kbd> today · <kbd>←</kbd> <kbd>→</kbd> change day
            </p>

            <div className="md-danger">
              <h4 className="md-danger__title">Delete everything</h4>
              <p className="md-danger__lead">
                Removes every activity, routine, habit, daily note and anything in the trash —
                {' '}{tasks.length + trash.length + routines.length + habits.length} records in all.
                This is the one thing here that cannot be undone.
              </p>
              <button className="md-btn md-btn--danger" onClick={() => { setWipeConfirm(''); setWipeOpen(true); }}>
                Delete all My Day data
              </button>
            </div>
          </section>
        )}

        {/* ── Views ──────────────────────────────────────────────────────── */}
        {view === 'today' && (
          <>
            <QuickAdd ref={quickRef} today={date} onAdd={addTask} />

            <label className="md__intention">
              <span className="md__intention-lbl">🎯 What would make {relativeDay(date, today).toLowerCase()} a win?</span>
              <input className="md-input" value={dayMeta.intention || ''}
                placeholder="One sentence — the thing you'd be glad you did."
                onChange={e => setDayMeta({ intention: e.target.value })} />
            </label>

            {load.plannedMin > 0 && (
              <div className={`md-load md-load--${load.state}`}>
                <span className="md-load__fill" style={{ width: `${Math.min(100, load.pct)}%` }} />
                <span className="md-load__txt">
                  {fmtDuration(load.plannedMin)} planned of {fmtDuration(load.capacityMin)} capacity · {load.pct}%
                  {load.state === 'over' && ' — over-committed, move something'}
                  {load.state === 'tight' && ' — a full day'}
                </span>
              </div>
            )}

            <div className="md__filters">
              <input id="md-search" className="md-input md-input--sm md__search" value={search}
                placeholder="Search activities…  ( / )" onChange={e => setSearch(e.target.value)} />
              <select className="md-input md-input--sm" value={catFilter} onChange={e => setCatFilter(e.target.value)} aria-label="Filter by category">
                <option value="all">All categories</option>
                {cats.map(c => <option key={c.id} value={c.id}>{c.icon} {c.name}</option>)}
              </select>
              <select className="md-input md-input--sm" value={priFilter} onChange={e => setPriFilter(e.target.value)} aria-label="Filter by priority">
                <option value="all">All priorities</option>
                {PRIORITIES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
              </select>
              <label className="md-toggle md-toggle--inline">
                <input type="checkbox" checked={showDone} onChange={e => setShowDone(e.target.checked)} />
                <span>Show completed</span>
              </label>
            </div>

            {dayTasks.length === 0 ? (
              <div className="md-empty md-empty--big">
                <span className="md-empty__icon" aria-hidden="true">🗒️</span>
                <h3>Nothing on {relativeDay(date, today).toLowerCase()} yet</h3>
                <p>Add the first activity above — plain English works: <code>order onions tomorrow 8am p1 @inventory ~20m</code></p>
              </div>
            ) : (
              <>
                <Group title="↪ Carried over" hint="Unfinished from earlier days — decide or do" items={groups.carried}
                  cats={cats} today={today} siblingsRef={siblingsRef} tone="warn"
                  action={stale.length >= 3 && (
                    <button className="md-btn md-btn--sm" onClick={() => setTidyOpen(true)}>
                      🧹 Tidy up {stale.length}
                    </button>
                  )}
                  onToggle={toggleTask} onOpen={t => setEditingId(t.id)} onAction={taskAction} />
                <Group title="🕐 Scheduled" hint="Blocked to a time" items={groups.scheduled}
                  cats={cats} today={today} siblingsRef={siblingsRef}
                  onToggle={toggleTask} onOpen={t => setEditingId(t.id)} onAction={taskAction} />
                <Group title="📋 Anytime" hint="Fit them in where they land" items={groups.anytime}
                  cats={cats} today={today} siblingsRef={siblingsRef}
                  onToggle={toggleTask} onOpen={t => setEditingId(t.id)} onAction={taskAction} />
                {showDone && (
                  <Group title="✅ Completed" hint={`${groups.done.length} done`} items={groups.done}
                    cats={cats} today={today} siblingsRef={siblingsRef}
                    action={(
                      <button className="md-btn md-btn--sm" onClick={clearCompleted}
                        title="Move them to the trash — restorable for 30 days">
                        Clear completed
                      </button>
                    )}
                    onToggle={toggleTask} onOpen={t => setEditingId(t.id)} onAction={taskAction} />
                )}
              </>
            )}
          </>
        )}

        {view === 'plan' && (
          <PlanView
            tasks={tasks} cats={cats} today={today} date={planDate} settings={settings}
            dayMeta={days[planDate] || {}}
            onSetDate={setPlanDate}
            onPick={pickIntoDay}
            onSchedule={scheduleTask}
            onOpen={t => setEditingId(t.id)}
            onToggle={toggleTask}
            onDayMeta={patch => setDays(prev => ({ ...prev, [planDate]: { ...(prev[planDate] || {}), ...patch } }))}
            onSettings={patch => setSettings(s2 => ({ ...s2, ...patch }))}
            onQuickAdd={addToDay}
          />
        )}

        {view === 'upcoming' && (
          <UpcomingView tasks={visible} cats={cats} today={today} settings={settings}
            onToggle={toggleTask} onOpen={t => setEditingId(t.id)} onAction={taskAction}
            onPick={d => { setPlanDate(d); setView('plan'); }} />
        )}

        {view === 'matrix' && (
          <MatrixView tasks={visible} cats={cats} today={today}
            onToggle={toggleTask} onOpen={t => setEditingId(t.id)} onAction={taskAction} />
        )}

        {view === 'habits' && (
          <HabitsView habits={habits} today={today} onChange={setHabits} />
        )}

        {view === 'routines' && (
          <RoutinesView routines={routines} cats={cats} today={today} onChange={setRoutines} />
        )}

        {view === 'review' && (
          <ReviewView tasks={tasks} date={date} today={today} days={days} dayMeta={dayMeta}
            onDayMeta={setDayMeta} onToggle={toggleTask} onAction={taskAction}
            onRestore={restoreShelved}
            trash={trash}
            onRestoreTrash={restoreTrashed}
            onPurgeTrash={purgeTrashed}
            onEmptyTrash={emptyTrash}
            onCloseDay={() => { setDayMeta({ closedAt: Date.now() }); flash('🌙 Day closed. Anything still open rolls to tomorrow.'); }} />
        )}

        {view === 'insights' && (
          <InsightsView tasks={tasks} cats={cats} today={today} days={days} />
        )}
      </div>

      {editing && (
        <TaskEditor
          task={editing} cats={cats} today={today}
          onSave={t => patchTask(t.id, t)}
          onClose={() => setEditingId(null)}
          onDelete={t => taskAction('delete', t)}
          onMakeRoutine={makeRoutineFromTask}
        />
      )}

      <FocusTimer
        session={focus} task={focusTask} settings={settings}
        onTick={tickFocus}
        onChange={changeFocus}
        onStop={stopFocus}
        onComplete={completeFocus}
      />

      {tidyOpen && (
        <>
          <div className="md-scrim" onClick={() => setTidyOpen(false)} aria-hidden="true" />
          <div className="md-dialog" role="dialog" aria-label="Tidy up carried work">
            <h3 className="md-dialog__title">🧹 Tidy up carried work</h3>
            <p className="md-dialog__lead">
              {stale.length} {stale.length === 1 ? 'activity has' : 'activities have'} rolled over
              {' '}{settings.staleAfterCarries} times or more. Something that keeps surviving the
              day usually needs a decision, not another morning on the list.
            </p>
            <ul className="md-dialog__list">
              {stale.slice(0, 8).map(t => (
                <li key={t.id}><span className="md-meta md-meta--carry">↪ ×{t.carryCount}</span> {t.title}</li>
              ))}
              {stale.length > 8 && <li className="md-hint">…and {stale.length - 8} more</li>}
            </ul>
            <div className="md-dialog__acts">
              <button className="md-btn" onClick={() => setTidyOpen(false)}>Leave them</button>
              <button className="md-btn" onClick={() => tidyUp('defer')}>
                → Push to next week
              </button>
              <button className="md-btn md-btn--primary" onClick={() => tidyUp('shelve')}>
                📥 Shelve them
              </button>
            </div>
            <p className="md-hint">
              Pushing gives them a clean run next week. Shelving takes them out of every view —
              they stay in the Review tab and can be brought back any time.
            </p>
          </div>
        </>
      )}

      {wipeOpen && (
        <>
          <div className="md-scrim" onClick={() => setWipeOpen(false)} aria-hidden="true" />
          <div className="md-dialog md-dialog--danger" role="dialog" aria-label="Delete all My Day data">
            <h3 className="md-dialog__title">Delete everything?</h3>
            <p className="md-dialog__lead">
              This wipes {tasks.length} {tasks.length === 1 ? 'activity' : 'activities'},
              {' '}{routines.length} {routines.length === 1 ? 'routine' : 'routines'},
              {' '}{habits.length} {habits.length === 1 ? 'habit' : 'habits'} and
              {' '}{trash.length} in the trash, along with every daily note and reflection.
              There is no undo and no copy kept.
            </p>
            <p className="md-hint">
              If you only want a clean slate for the list itself, close this and use
              <strong> Clear completed</strong> or <strong>Tidy up</strong> instead — both are reversible.
              Export a CSV first if you'd like a record.
            </p>
            <label className="md-field">
              <span className="md-field__lbl">Type DELETE to confirm</span>
              <input className="md-input" value={wipeConfirm} autoFocus
                onChange={e => setWipeConfirm(e.target.value)} placeholder="DELETE" />
            </label>
            <div className="md-dialog__acts">
              <button className="md-btn" onClick={() => setWipeOpen(false)}>Keep my data</button>
              <button className="md-btn md-btn--sm" onClick={exportCsv}>⬇ Export CSV first</button>
              <button className="md-btn md-btn--danger" disabled={wipeConfirm !== 'DELETE'} onClick={wipeEverything}>
                Delete everything
              </button>
            </div>
          </div>
        </>
      )}

      {toast && (
        <div className="md__toast" role="status">
          <span>{toast.msg}</span>
          {toast.undo && (
            <button className="md__toast-undo" onClick={() => {
              const fn = undoRef.current;
              undoRef.current = null;
              if (fn) fn();
            }}>Undo</button>
          )}
        </div>
      )}
    </div>
  );
}

/** A labelled block of rows, hidden entirely when it has nothing in it. */
function Group({ title, hint, items, cats, today, tone, action, siblingsRef, onToggle, onOpen, onAction }) {
  if (!items.length) return null;
  const ids = items.map(t => t.id);
  return (
    <section className={`md-group${tone ? ` md-group--${tone}` : ''}`}>
      <header className="md-group__head">
        <h3 className="md-group__title">{title}</h3>
        <span className="md-group__hint">{hint}</span>
        {action || null}
        <span className="md-group__count">{items.length}</span>
      </header>
      <ul className="md-list">
        {items.map(t => (
          <TaskRow key={t.id} task={t} cats={cats} today={today}
            showDate={diffDays(t.date, today) !== 0}
            canMoveUp={canMove(t.id, -1, ids)}
            canMoveDown={canMove(t.id, 1, ids)}
            onToggle={onToggle} onOpen={onOpen}
            onAction={(act, task) => { siblingsRef.current = ids; onAction(act, task); }} />
        ))}
      </ul>
    </section>
  );
}
